import { Link } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

import { PublicPageShell } from '@/src/components/PublicPageShell';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

const refundSections = [
  {
    title: 'Overview',
    body: [
      'This Refund Policy explains when Frienzzi Inc refunds the optional verified account processing fee. It applies only to the verified account fee paid through Frienzzi for the optional public verified badge flow.',
      'Normal Frienzzi account access does not require this payment. You can use Frienzzi without buying verified account processing or earning a verified badge.',
    ],
  },
  {
    title: 'Verified account processing fee',
    body: [
      'The verified account processing fee is $10 plus applicable tax where supported. The fee is paid through Stripe Checkout and helps cover payment processing, tax calculation, abuse prevention, and identity verification workflow costs.',
      'Payment does not guarantee identity approval or a public verified badge. A verified badge also requires a verified email address, a verified phone number linked through Firebase Authentication, and a successful Stripe Identity document and selfie verification.',
      'One successful payment unlocks identity verification attempts for that Frienzzi account, subject to Frienzzi abuse-prevention and operational limits.',
    ],
  },
  {
    title: 'Refunds available before identity verification starts',
    body: [
      'You can request an automatic refund in the verification wizard if you paid the verified account processing fee but have not started Stripe Identity verification.',
      'Stripe Identity verification starts when Frienzzi creates or opens a Stripe Identity verification session for your account so you can submit identity document or selfie information.',
      'If the automatic refund button is available in the app, use it first. If the button is not available but you believe you qualify for a refund, contact support@frienzzi.com from the email address on your Frienzzi account.',
    ],
  },
  {
    title: 'Other refundable cases',
    body: [
      'Frienzzi will also refund the verified account processing fee when the same Frienzzi account is charged more than once for the same verified account processing step.',
      'Frienzzi will refund the fee when a Frienzzi, Firebase, Stripe Checkout, or Stripe Identity issue prevents identity verification from starting after payment and Frienzzi cannot reasonably fix or retry the issue.',
      'Frienzzi will refund the fee when payment is attached to the wrong account because of a Frienzzi support, engineering, or operational error.',
      'Frienzzi will refund the fee when a refund is required by applicable law.',
    ],
  },
  {
    title: 'Generally non-refundable after identity verification starts',
    body: [
      'After Stripe Identity verification starts, the verified account processing fee is generally non-refundable except for duplicate charges, Frienzzi or provider errors that prevent the process from starting correctly, wrong-account operational errors, or refunds required by law.',
      'The fee is generally not refundable if you start Stripe Identity and then abandon the process, change your mind, refuse to provide required identity document or selfie information, or do not complete the requested steps.',
      'The fee is generally not refundable if Stripe Identity requires more input, rejects the identity check, cannot verify your identity, or returns a result that does not qualify your account for a verified badge.',
      'The fee is generally not refundable if you later lose badge eligibility because your email, phone, identity, account standing, payment, or refund status no longer qualifies.',
    ],
  },
  {
    title: 'Refund effects on verified status',
    body: [
      'A refunded payment removes or suspends the paid verified-account eligibility for that Frienzzi account.',
      'If your verified account fee is refunded, you may need to pay again before starting or continuing identity verification.',
      'If a refund is issued after a badge has been granted, Frienzzi may remove or suspend the public verified badge unless another valid paid eligibility record exists.',
    ],
  },
  {
    title: 'Timing, taxes, and payment-method rules',
    body: [
      'Approved refunds are sent through Stripe to the original payment method when available. Your bank, card network, wallet, or payment provider controls how long the refund takes to appear.',
      'Taxes, card fees, wallet fees, currency conversion, and payment-method charges are handled according to Stripe, payment-provider, and applicable legal rules. Some amounts may be delayed, adjusted, or non-refundable where a payment provider or law requires that result.',
      'Frienzzi stores only the payment, refund, and verification status information needed to operate the verified account flow, prevent duplicate charges, handle support, and comply with legal obligations.',
    ],
  },
  {
    title: 'How to request help',
    body: [
      'Start with the automatic refund option in the verification wizard if it is available.',
      'For refund problems the app cannot resolve, email support@frienzzi.com from the email address on your Frienzzi account. Include your username, the approximate payment date, and a short explanation of what happened. Do not email identity document images, card numbers, or sensitive payment details.',
      'Frienzzi may ask you to confirm account ownership before reviewing a refund issue.',
    ],
  },
  {
    title: 'Policy changes',
    body: [
      'Frienzzi may update this Refund Policy as the verified account flow, payment providers, tax handling, or legal requirements change.',
      'When a meaningful refund policy change affects users, Frienzzi will publish a plain-language public changelog entry.',
    ],
  },
];

export default function RefundPolicyScreen() {
  return (
    <PublicPageShell
      title="Refund Policy"
      subtitle="Verified account processing fee refunds for Frienzzi."
    >
      <View style={styles.notice}>
        <Text style={styles.noticeTitle}>Short version</Text>
        <Text style={styles.copy}>
          The optional verified account fee is refundable before Stripe Identity
          starts. After identity verification starts, it is generally
          non-refundable except for duplicate charges, Frienzzi or provider
          errors, wrong-account operational errors, or refunds required by law.
        </Text>
      </View>
      <View style={styles.panel}>
        {refundSections.map((section) => (
          <View key={section.title} style={styles.section}>
            <Text style={styles.heading}>{section.title}</Text>
            {section.body.map((paragraph) => (
              <Text key={paragraph} style={styles.copy}>
                {paragraph}
              </Text>
            ))}
          </View>
        ))}
      </View>
      <Text style={styles.copy}>
        This policy should be read with the{' '}
        <Link href="/terms" style={styles.link}>
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link href="/privacy" style={styles.link}>
          Privacy Policy
        </Link>
        .
      </Text>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  copy: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  heading: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  link: {
    color: colors.primary,
    fontWeight: '800',
  },
  notice: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.primary,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  noticeTitle: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xl,
    padding: spacing.lg,
  },
  section: {
    gap: spacing.sm,
  },
});
